<template>
  <div>
    <section v-if="currentRound" class="activity-stage quotebubble">
      <aside class="activity-side-image">
        <figure>
          <img :src="currentRound.leftImage?.image.src" />
        </figure>
      </aside>

      <section class="activity-content-container">
        <header class="activity-header-quote">
          <text-display-list>
            <li :class="{ isMobileView: isMobile }" class="quote-bubble-container">
              <div :style="quoteBubbleImage">
                <blinking-cursor-text
                  :text="currentRound.quote"
                  :text-step="0"
                ></blinking-cursor-text>
              </div>
            </li>
          </text-display-list>
        </header>

        <div class="activity-subheader">
          <text-display-list class="subheader-words">
            <li>
              <h3>English Word:</h3>
              <blinking-cursor-text :text="currentRound.word" :text-step="1"></blinking-cursor-text>
            </li>

            <li>
              <h3>Meaning:</h3>
              <blinking-cursor-text
                :text="currentRound.meaning"
                :text-step="2"
              ></blinking-cursor-text>
            </li>
          </text-display-list>
        </div>

        <div class="activity-paragraph">
          <figure>
            <img
              :style="{ height: 'auto', width: isMobile ? '75px' : '150px' }"
              :src="currentRound.centerImage?.image.src"
            />
          </figure>

          <blinking-cursor-text
            :text="currentRound.paragraph"
            :text-step="3"
          ></blinking-cursor-text>
        </div>
      </section>
    </section>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TimelineLite, TweenMax } from 'gsap';
import { createNamespacedHelpers } from 'vuex';
import quotebubble from 'assets/svg/quotebubble.svg';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

const application = createNamespacedHelpers('application');

export default {
  components: {
    BlinkingCursorText,
    TextDisplayList
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'QuoteBubble',
      quoteBubbleImage: {
        backgroundImage: `url('data:image/svg+xml;utf8,${quotebubble}')`
      }
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    ...application.mapState({
      isMobile: state => state.device.isMobile
    }),

    isEndOfRound() {
      return this.progress.stepIndex === 4;
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ leftImage, centerImage }) => {
        this.queueImage(leftImage.image.src);
        this.queueImage(centerImage.image.src);
      });

      return this.downloadImages();
    },

    getTextForStep() {
      const { stepIndex } = this.progress;
      if (stepIndex === 0) return this.currentRound.quote;
      if (stepIndex === 1) return this.currentRound.word;
      if (stepIndex === 2) return this.currentRound.meaning;
      return this.currentRound.paragraph;
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          voice: `${this.voiceInstructionsPrefix}/whatsitmean-${this.getRandomVoice()}-1.mp3`,
          written: 'Type the sentences, words, and phrases.',
          delay: 4
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.overwriteProgressText({
        currentText: this.getTextForStep()
      });

      this.$nextTick(() => {
        this.hideStep();
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.handleStepEnd();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.sndBell();
      this.finishStep();
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();

      const nextText = this.getTextForStep();
      this.overwriteProgressText({ currentText: nextText });

      if (this.progress.stepIndex === 3) this.fadeInStep();
    },

    fadeInStep() {
      TweenMax.to('.activity-paragraph', 0.5, { opacity: 1 });
    },

    hideStep() {
      TweenMax.set('.activity-paragraph', { opacity: 0 });
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .delay(1)
          .to('.activity-stage', 0.5, { opacity: 0 })
          .call(this.setupNewRound)
          .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.quotebubble {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;

  header {
    background-color: transparent;
  }

  .activity-side-image {
    margin-top: 3em;

    figure {
      -webkit-margin-end: 20px;
    }
  }

  .activity-content-container {
    width: 100%;
  }

  ul {
    list-style: none;
    -webkit-padding-start: 0;
    margin: 0;
  }

  .activity-header-quote {
    width: 705px;
    text-align: left;

    ul {
      margin: 1.5em 1em 0 0;
    }
  }
  .quote-bubble-container {
    width: 100%;
    height: 130px;

    div {
      display: flex;
      height: 95px;
      background-repeat: no-repeat;
      padding: 0.8em 1em;

      p {
        display: block;
        padding-bottom: 1.2em;
      }
    }
  }

  .quote-bubble-container.isMobileView {
    left: -2rem;
    position: relative;
    top: 1.5rem;
  }

  .subheader-words,
  .activity-paragraph {
    display: flex;
    flex-flow: row nowrap;
  }

  .activity-subheader {
    padding-left: 4em;

    h3 {
      font-size: 0.9em;
      font-weight: bold;
      margin: 0;
    }

    li {
      margin-right: 2em;
    }
  }

  .activity-paragraph {
    align-items: center;
    padding-right: 2em;

    p {
      display: block;
      height: 100%;
    }
  }

  p {
    font-size: 20px;
  }
}
</style>
